/**
 * Контракт советника: что модель получает вместо доступа к данным.
 *
 * Правило одно и жёсткое: **любое число в любой фразе приходит из ядра**.
 * Модель получает посчитанный контекст и закрытый перечень инструментов, а
 * не операции и балансы. Нет данных для самостоятельного счёта — нет и
 * выдуманных чисел, а с ними и класса отказов «приложение сказало, что могу,
 * а денег не хватило».
 *
 * Второе правило — проверяемость. Если модель недоступна, тот же вопрос
 * отвечается шаблоном по тому же контексту: правильно, но суховато. Интент,
 * который без модели не отвечает вовсе, означает, что расчёт утёк в модель.
 */
import { ZERO, clampPos, formatMoney, sub, type Money } from './money.js';
import { type PlainDate } from './date.js';
import { nextForStream } from './income.js';
import { isKnown, type Amount } from './amount.js';
import { computeAvailable, type AvailableInput, type AvailableResult } from './available.js';
import { computeRunway, computeLiquidity, type RunwayResult } from './runway.js';
import { computeFinancialProfile, missingFacts, type FinancialProfile } from './profile.js';
import { prioritize, type Priority, type PriorityStack } from './priority.js';
import { runScenario, type ActionScenario, type ScenarioResult } from './scenario.js';
import { paymentInstances, type PaymentInstance } from './instances.js';
import { cardPayments, forecast } from './forecast.js';
import type { GoalEventLike } from './profile.js';
import type { Loan } from './types.js';
import type { PayoutCadence, StableOutlook, StableWarning } from './stable.js';

/**
 * Снимок картины для советника.
 *
 * Только посчитанное: суммы, даты, состояния и объяснения. Сырых операций
 * здесь нет — они модели не нужны и опасны.
 */
export interface AdvisorContext {
  today: PlainDate;
  /** Версия данных: ответ, посчитанный на устаревшей, не применяется. */
  stateVersion: string;
  /**
   * Полная картина для модели: счета, карты с условиями, доходы, платежи с
   * оплатами, конверты, цели, обычные траты и операции — в рублях и
   * словами. Собирает клиент, у него названия; ядро её не читает.
   */
  data?: Record<string, unknown>;

  available: {
    amount: Money;
    perDay: Money;
    horizonDate: PlainDate;
    daysToHorizon: number;
    /** Построчный разбор — из него берутся объяснения. */
    deductions: { label: string; amount: Money; kind: string }[];
  };
  runway: { known: boolean; days?: number; until?: PlainDate | null; action?: string };
  profile: FinancialProfile;
  priorities: {
    primary: PrioritySummary;
    next: PrioritySummary[];
    later: PrioritySummary[];
  };
  payments: {
    label: string; dueDate: PlainDate; amount: Money; secured: Money; settled: boolean;
  }[];
  /**
   * Стабильный бюджет, если он включён.
   *
   * Советник обязан знать о нём, иначе он посоветует «растянуть деньги до
   * зарплаты» тому, у кого зарплаты нет вовсе. И обязан понимать, что
   * выплата себе — не доход: без этой строки он сочтёт буфер свободными
   * деньгами и предложит их потратить.
   */
  stable?: {
    buffer: Money;
    payout: Money;
    cadence: PayoutCadence;
    nextPayout: PlainDate | null;
    payoutsLeft: number;
    exhaustsOn: PlainDate | null;
    warning: StableWarning;
    note: string;
  };
  /** Чего не хватает для полной картины — модель спрашивает именно об этом. */
  missing: string[];
  /**
   * Счета: имя, вид, остаток и считаются ли деньги для трат.
   *
   * Без них на вопрос «с какого счёта заплатить» или «хватит ли на карте»
   * модели нечем ответить, и она пересказывает приоритеты.
   */
  accounts: { name: string; kind: string; balance: Money; forSpending: boolean }[];
  /**
   * Кредитные карты — с условиями, от которых зависит ответ на «а если
   * картой?»: день выписки, день платежа, льготный срок, лимит, что внести
   * по текущей выписке и сколько под это отложено.
   */
  cards: {
    id: string; name: string; debt: Money; limit: Money; availableLimit: Money;
    toPay: Money; payBy: PlainDate; setAside: Money; missing: Money;
    statementDay: number | null; paymentDay: number; graceDays: number;
    /**
     * Покупки после выписки и срок их платежа — из прогноза.
     *
     * Выписку человек вводит руками, и следующая сама не появляется. Без этих
     * полей на «сколько платить по карте в ноябре» модели нечем ответить:
     * у владельца это 50 217,61 ₽ к 9 ноября.
     */
    afterStatement: Money; afterStatementBy: PlainDate | null;
  }[];
  /** Доходы по ритму: сколько и когда ближайшее поступление. */
  incomes: { name: string; amount: Money; variable: boolean; next: PlainDate | null }[];
  /**
   * Числа главной — ровно те, что человек видит.
   *
   * Советник, отвечающий другими суммами, чем главная, выглядит как второе
   * приложение: на главной «можно тратить 8 188», а в ответе «есть только
   * 4 601». Считает их клиент (раскладка живёт у него), ядро только передаёт.
   */
  home?: HomeFacts;
  /**
   * Идентификаторы, на которые можно ссылаться в расчётах.
   *
   * Без них модель называет долг словом «кредитка», а инструмент ждёт `id`
   * и отказывает. Это не данные о деньгах: только имена и ключи.
   */
  refs: {
    loans: { id: string; name: string }[];
    goals: {
      id: string; name: string; target: Money; saved: Money;
      by: PlainDate | null; monthly: Money; status: string;
    }[];
  };
}

/** Что показывает главная: ответ, три числа и «После прихода». */
export interface HomeFacts {
  /** До какой даты считается «можно тратить»; `null` — даты нет. */
  spendUntil: PlainDate | null;
  /** Сколько можно тратить до этой даты — сумма корзин. */
  spend: Money;
  perDay: Money;
  onAccounts: Money;
  setAside: Money;
  notSetAside: Money;
  afterIncome?: {
    from: PlainDate; to: PlainDate;
    income: Money; payments: Money; living: Money | null; left: Money | null;
  };
}

export interface PrioritySummary {
  kind: string;
  title: string;
  why: string;
  amount: Money;
  action: string;
  dueDate?: PlainDate;
  execution?: string;
}

const summarize = (p: Priority): PrioritySummary => ({
  kind: p.kind,
  title: p.title,
  why: p.why,
  amount: p.amount,
  action: p.action,
  ...(p.target?.dueDate ? { dueDate: p.target.dueDate } : {}),
  ...(p.target?.execution ? { execution: p.target.execution } : {}),
});

export interface AdvisorContextInput extends AvailableInput {
  stateVersion: string;
  obligationsKnown?: boolean;
  needBaseline: Amount;
  wantBaseline: Amount;
  loans?: Loan[];
  goalEvents?: readonly GoalEventLike[];
  transactions?: readonly import('./analytics.js').AnalyticsTx[];
  /** Прогноз буфера, если стабильный бюджет включён. */
  stable?: StableOutlook;
  /** Числа главной: считает клиент, у него раскладка. */
  home?: HomeFacts;
  /** Полная картина для модели — см. `AdvisorContext.data`. */
  data?: Record<string, unknown>;
}

export function advisorContext(input: AdvisorContextInput): AdvisorContext {
  const available: AvailableResult = computeAvailable(input);
  const needMoney = isKnown(input.needBaseline) ? input.needBaseline.value : ZERO;

  const runway: RunwayResult = computeRunway({
    today: input.today,
    accounts: input.accounts,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    obligations: input.obligations,
    needBaseline: input.needBaseline,
    wantBaseline: input.wantBaseline,
    creditTerms: input.creditTerms,
    ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
  });
  const cardEvents = cardPayments({
    today: input.today, accounts: input.accounts, creditTerms: input.creditTerms,
    ...(input.reserves ? { reserves: input.reserves } : {}),
  });

  const profile = computeFinancialProfile({
    today: input.today,
    accounts: input.accounts,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    obligationsKnown: input.obligationsKnown === true,
    ...(input.loans ? { loans: input.loans } : {}),
    creditTerms: input.creditTerms,
    goals: input.goals,
    ...(input.goalEvents ? { goalEvents: input.goalEvents } : {}),
    ...(input.transactions ? { transactions: input.transactions } : {}),
    needBaseline: input.needBaseline,
    wantBaseline: input.wantBaseline,
  });

  const stack: PriorityStack = prioritize({
    today: input.today,
    incomeStreams: input.incomeStreams,
    obligations: input.obligations,
    needBaseline: needMoney,
    goals: input.goals,
    loans: input.loans ?? [],
    available,
    accounts: input.accounts,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    needBaselineAmount: input.needBaseline,
    wantBaselineAmount: input.wantBaseline,
  });

  const payments: PaymentInstance[] = paymentInstances({
    obligations: input.obligations,
    ...(input.reserves ? { reserves: input.reserves } : {}),
    today: input.today,
    from: input.today,
    to: available.horizonDate,
  });

  return {
    today: input.today,
    stateVersion: input.stateVersion,
    ...(input.data ? { data: input.data } : {}),
    available: {
      amount: available.available,
      perDay: available.perDay,
      horizonDate: available.horizonDate,
      daysToHorizon: available.daysToHorizon,
      deductions: available.deductions.map((d) => ({
        label: d.label, amount: d.amount, kind: d.kind,
      })),
    },
    runway: runway.known
      ? { known: true, days: runway.days, until: runway.until }
      : { known: false, action: runway.action },
    profile,
    priorities: {
      primary: summarize(stack.primary),
      next: stack.next.map(summarize),
      later: stack.later.map(summarize),
    },
    payments: payments.map((p) => ({
      label: p.label, dueDate: p.scheduledDate, amount: p.amount,
      secured: p.secured, settled: p.settled,
    })),
    ...(input.stable
      ? {
        stable: {
          buffer: input.stable.buffer,
          payout: input.stable.payout,
          cadence: input.stable.cadence,
          nextPayout: input.stable.due?.scheduledDate ?? null,
          payoutsLeft: input.stable.payoutsLeft,
          exhaustsOn: input.stable.exhaustsOn,
          warning: input.stable.warning,
          note: 'Выплата себе — движение собственных денег из буфера, а не доход. '
            + 'Ни включать режим, ни менять сумму, ни переводить деньги ты не можешь: '
            + 'это делает человек.',
        },
      }
      : {}),
    missing: missingFacts(profile),
    accounts: input.accounts.filter((a) => !a.archived).map((a) => ({
      name: a.name, kind: a.kind, balance: a.balance,
      forSpending: a.isLiquid && a.kind !== 'credit_card',
    })),
    cards: available.credit.map((c) => {
      const t = input.creditTerms.find((x) => x.accountId === c.accountId);
      const after = cardEvents.find(
        (e) => e.ref === c.accountId && e.card?.part === 'after_statement',
      );
      return {
        id: c.accountId, name: c.name, debt: c.debt, limit: c.limit,
        availableLimit: c.availableLimit, toPay: c.needForGrace, payBy: c.paymentDate,
        setAside: clampPos(sub(c.needForGrace, c.missing)), missing: c.missing,
        statementDay: t?.statementDay ?? null, paymentDay: t?.paymentDay ?? 0,
        graceDays: t?.graceDays ?? 0,
        afterStatement: after?.amount ?? ZERO, afterStatementBy: after?.date ?? null,
      };
    }),
    incomes: input.incomeStreams.filter((x) => x.isActive).map((x) => ({
      name: x.name, amount: x.amount, variable: x.isVariable, next: nextForStream(x, input.today),
    })),
    ...(input.home ? { home: input.home } : {}),
    refs: {
      loans: (input.loans ?? []).map((l) => ({ id: l.id, name: l.name })),
      goals: input.goals.filter((g) => g.status === 'active' || g.status === 'paused').map((g) => ({
        id: g.id, name: g.name, target: g.targetAmount, saved: g.currentAmount,
        by: g.targetDate ?? null, monthly: g.monthlyContribution, status: g.status,
      })),
    },
  };
}

/* ─────────────────────────  Инструменты  ───────────────────────── */

/**
 * Закрытый перечень того, что модель может попросить посчитать.
 *
 * Имя вне списка — отказ, а не попытка угадать. Произвольных запросов к
 * данным, SQL и кода здесь нет и быть не может.
 */
export type ToolName =
  | 'check_purchase'
  | 'check_debt_payoff'
  | 'check_goal_change'
  | 'payment_calendar'
  | 'data_quality';

export interface ToolCall {
  /** Идентификатор вызова: по нему результат сопоставляется с запросом. */
  id: string;
  name: ToolName;
  args: Record<string, unknown>;
}

export interface ToolResult {
  id: string;
  name: ToolName;
  /** Версия данных, на которой посчитано. Устаревший результат не применяется. */
  stateVersion: string;
  /** Числа и даты — только отсюда они и попадают в ответ. */
  facts: Record<string, Money | number | string | null>;
  /** Чего расчёт не знает. Эти строки нельзя потерять при пересказе. */
  limits: string[];
  /** Варианты с посчитанными последствиями. */
  alternatives?: { label: string; effect: string }[];
  /**
   * Какие из фактов — деньги (в копейках).
   *
   * Модели они отдаются в рублях. Прежде факты уходили к ней как есть, и
   * переводить копейки она бралась сама: ответы с «87 000» вместо 870 000 ₽
   * отклонялись проверкой чисел, и человек видел «не получилось».
   */
  moneyKeys?: string[];
}

/**
 * Предложение, которое можно применить.
 *
 * Разбор сам по себе ничего не меняет: он считает гипотезу. Чтобы гипотеза
 * стала фактом, нужно действие человека — и для этого расчёт должен
 * превратиться в понятное «что именно изменится». Иначе совет остаётся
 * текстом, который человек переносит в приложение руками, теряя по дороге
 * половину чисел.
 *
 * Строится из **вызова инструмента**, а не из слов модели: применяется то,
 * что было посчитано, и ровно теми числами, которые человек видел.
 */
export type Proposal =
  /** Изменить условия цели. */
  | { kind: 'goal_change'; goalId: string; monthlyContribution?: Money; targetDate?: PlainDate }
  /** Записать покупку — форма откроется заполненной. */
  /** `card` — покупка кредиткой: запишется на карту, платить — по выписке. */
  | { kind: 'purchase'; amount: Money; date?: PlainDate; card?: string }
  /** Внести досрочно в долг. */
  | { kind: 'debt_payoff'; loanId: string; amount: Money };

/**
 * Предложение из вызова инструмента.
 *
 * `null` означает, что применять нечего: календарь платежей и проверка
 * качества данных — это сведения, а не изменение. Предлагать кнопку
 * «Применить» там, где применять нечего, значит обещать несуществующее.
 */
export function proposalFrom(call: ToolCall): Proposal | null {
  switch (call.name) {
    case 'check_goal_change': {
      const goalId = asId(call.args.goalId);
      if (!goalId) return null;
      const monthly = call.args.monthlyContribution === undefined
        ? null : asMoney(call.args.monthlyContribution);
      const targetDate = call.args.targetDate === undefined ? null : asDate(call.args.targetDate);
      if (monthly === null && targetDate === null) return null;
      return {
        kind: 'goal_change', goalId,
        ...(monthly !== null ? { monthlyContribution: monthly } : {}),
        ...(targetDate ? { targetDate } : {}),
      };
    }
    case 'check_purchase': {
      const amount = asMoney(call.args.amount);
      if (amount === null || amount <= ZERO) return null;
      const date = call.args.date === undefined ? null : asDate(call.args.date);
      const card = call.args.card === undefined ? null : asId(call.args.card);
      return { kind: 'purchase', amount, ...(date ? { date } : {}), ...(card ? { card } : {}) };
    }
    case 'check_debt_payoff': {
      const amount = asMoney(call.args.amount);
      const loanId = asId(call.args.loanId);
      if (amount === null || amount <= ZERO || !loanId) return null;
      return { kind: 'debt_payoff', loanId, amount };
    }
    default:
      return null;
  }
}

export interface ToolError {
  id: string;
  name: string;
  error: string;
}

const asMoney = (v: unknown): Money | null =>
  (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) as Money : null);

const asDate = (v: unknown): PlainDate | null =>
  (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v as PlainDate : null);

const asId = (v: unknown): string | null =>
  (typeof v === 'string' && v.length > 0 && v.length <= 64 ? v : null);

export interface ToolRunInput extends AdvisorContextInput {
  /** Контекст уже посчитан: инструменты работают на тех же данных. */
  context: AdvisorContext;
}

/**
 * Выполнить вызов инструмента.
 *
 * Аргументы проверяются здесь, а не в модели: модель их предлагает, но
 * доверять предложению нельзя. Неизвестное имя, кривой аргумент и чужая
 * версия данных дают отказ с причиной.
 */
export function runTool(input: ToolRunInput, call: ToolCall): ToolResult | ToolError {
  const fail = (error: string): ToolError => ({ id: call.id, name: call.name, error });

  switch (call.name) {
    case 'check_purchase': {
      const amount = asMoney(call.args.amount);
      if (amount === null || amount <= ZERO) return fail('Нужна положительная сумма покупки');
      const date = call.args.date === undefined ? null : asDate(call.args.date);
      if (call.args.date !== undefined && date === null) return fail('Дата в формате ГГГГ-ММ-ДД');
      // Покупка кредиткой: деньги уйдут в день платежа по выписке, а не в день
      // покупки. Карта называется идентификатором из «кредитки».
      const card = call.args.card === undefined ? null : asId(call.args.card);
      if (call.args.card !== undefined) {
        const known = card !== null && input.accounts.some((a) => a.id === card && a.kind === 'credit_card')
          && input.creditTerms.some((t) => t.accountId === card);
        if (!known) return fail('Нет такой кредитки — возьми id из списка «кредитки»');
      }
      return scenarioResult(input, call, {
        kind: 'purchase', amount, ...(date ? { date } : {}), ...(card ? { card } : {}),
      });
    }

    case 'check_debt_payoff': {
      const amount = asMoney(call.args.amount);
      const loanId = asId(call.args.loanId);
      if (amount === null || amount <= ZERO) return fail('Взнос должен быть положительным');
      if (!loanId) return fail('Нужен идентификатор долга');
      // Погашение — не покупка с отрицательной суммой: деньги уходят, а долг
      // уменьшается, и это два разных эффекта.
      return scenarioResult(input, call, { kind: 'debt_payoff', loanId, amount });
    }

    case 'check_goal_change': {
      const goalId = asId(call.args.goalId);
      if (!goalId) return fail('Нужен идентификатор цели');
      const monthly = call.args.monthlyContribution === undefined
        ? null : asMoney(call.args.monthlyContribution);
      const targetDate = call.args.targetDate === undefined ? null : asDate(call.args.targetDate);
      return scenarioResult(input, call, {
        kind: 'goal_change',
        goalId,
        ...(monthly !== null ? { monthlyContribution: monthly } : {}),
        ...(targetDate ? { targetDate } : {}),
      });
    }

    case 'payment_calendar': {
      const to = asDate(call.args.to);
      if (!to) return fail('Нужна конечная дата в формате ГГГГ-ММ-ДД');
      /*
        Платежи до даты — из общего прогноза: обязательные и по кредиткам.

        Прежде здесь были только обязательства, и на «сколько уйдёт на
        платежи в октябре» советник отвечал без кредитки — у владельца без
        149 374,93 ₽ по выписке 9 октября. Уже оплаченное не входит: оно
        ушло со счёта, и «уйдёт» о нём не скажешь.
      */
      // Доходы и жизнь календарю не нужны: он спрашивает только о платежах.
      const list = forecast({
        today: input.today,
        to,
        accounts: input.accounts,
        creditTerms: input.creditTerms,
        incomeStreams: [],
        obligations: input.obligations,
        ...(input.reserves ? { reserves: input.reserves } : {}),
        needBaseline: ZERO,
        wantBaseline: ZERO,
        ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
      }).events.filter((e) => e.kind === 'payment' || e.kind === 'card');
      const facts: Record<string, Money | number | string | null> = {
        count: list.length,
        total: list.reduce((s, e) => (s + e.amount) as Money, ZERO),
        unsecured: list.filter((e) => e.amount > e.covered).length,
      };
      list.slice(0, 10).forEach((e, n) => {
        facts[`payment_${n}`] = `${e.label} ${formatMoney(e.amount)} ${e.date}`;
      });
      return {
        id: call.id, name: call.name, stateVersion: input.stateVersion, facts, limits: [],
        moneyKeys: ['total'],
      };
    }

    case 'data_quality': {
      const q = input.context.profile.dataQuality;
      return {
        id: call.id,
        name: call.name,
        stateVersion: input.stateVersion,
        facts: {
          incomeKnown: String(q.incomeKnown),
          expensesKnown: String(q.expensesKnown),
          obligationsKnown: String(q.obligationsKnown),
          missing: input.context.missing.join(', ') || 'ничего',
        },
        limits: input.context.missing.length > 0
          ? [`Не хватает данных: ${input.context.missing.join(', ')}`]
          : [],
      };
    }

    default:
      return fail(`Инструмент «${String((call as ToolCall).name)}» не разрешён`);
  }
}

function scenarioResult(
  input: ToolRunInput,
  call: ToolCall,
  scenario: ActionScenario,
): ToolResult {
  const needMoney = isKnown(input.needBaseline) ? input.needBaseline.value : ZERO;
  const wantMoney = isKnown(input.wantBaseline) ? input.wantBaseline.value : ZERO;

  const r: ScenarioResult = runScenario({
    ...input,
    amount: ZERO,
    needBaseline: needMoney,
    wantBaseline: wantMoney,
    ...(input.loans ? { loans: input.loans } : {}),
  }, scenario);

  const facts: Record<string, Money | number | string | null> = {
    verdict: r.verdict,
    leftAfter: r.effects.leftAfter,
    minBalance: r.effects.minBalance,
    minBalanceDate: r.effects.minBalanceDate,
  };
  if (r.effects.debtAfter !== undefined) facts.debtAfter = r.effects.debtAfter;
  if (r.effects.savedPerYear !== undefined) facts.savedPerYear = r.effects.savedPerYear;
  if (r.effects.goalEta !== undefined) facts.goalEta = r.effects.goalEta;
  if (r.effects.monthlyNeeded !== undefined) facts.monthlyNeeded = r.effects.monthlyNeeded;
  if (r.effects.card) {
    facts.cardStatementDate = r.effects.card.statementDate;
    facts.cardPaymentDate = r.effects.card.paymentDate;
    facts.cardWithinGrace = r.effects.card.withinGrace ? 'да' : 'нет';
    facts.cardLimitLeft = r.effects.card.limitLeft;
  }

  // Неизвестные расходы — ограничение ответа, а не повод подставить ноль.
  const limits = [...r.limits];
  if (!isKnown(input.needBaseline)) {
    limits.push('Обычные траты неизвестны — прогноз построен без них');
  }

  return {
    id: call.id,
    name: call.name,
    stateVersion: input.stateVersion,
    facts,
    moneyKeys: ['leftAfter', 'minBalance', 'debtAfter', 'savedPerYear', 'monthlyNeeded', 'cardLimitLeft'],
    limits,
    alternatives: r.alternatives.map((a) => ({ label: a.label, effect: a.effect })),
  };
}

/* ────────────────────  Ответ без модели (правило Л4)  ──────────────────── */

export interface AdvisorAnswer {
  /** Главный вывод одной фразой. */
  headline: string;
  /**
   * Для ответа не хватает данных: `basis` — вопрос модели, `limits` — чего
   * не хватает. Онбординг по ней показывает вопрос и кнопки к нужным шагам —
   * поля для ответа там нет.
   */
  insufficient?: boolean;
  /** Тон вывода: значок рядом с ним. Нет — ответ короткий или старый. */
  tone?: 'yes' | 'careful' | 'no' | 'info';
  /** Что проверяли: сумма, дата или период, способ оплаты. */
  conditions?: { label: string; value: string }[];
  /** Почему расчёт шире вопроса. */
  conditionsNote?: string;
  /** До трёх главных последствий. */
  figures?: { label: string; value: string; note?: string; tone?: 'ok' | 'warn' | 'bad' }[];
  /** Ограничение, способное изменить вывод: видно сразу. */
  warning?: string;
  /** Как посчитано: шаги и допущения. */
  details?: string[];
  /** Короткое основание — откуда взялся вывод. */
  basis: string;
  /** Что сделать первым. */
  action: string;
  /** Ограничения расчёта. Теряться при пересказе не имеют права. */
  limits: string[];
  alternatives: { label: string; effect: string }[];
  /**
   * Что в порядке и делать ничего не нужно.
   *
   * Разбор, состоящий из одних проблем, оставляет человека в тревоге, даже
   * когда четыре платежа из пяти обеспечены. Спокойствие — такой же результат
   * расчёта, как и предупреждение, и умалчивать о нём нечестно.
   */
  fine?: string[];
  /** Ответ собран без модели: так и называется на экране. */
  withoutModel: boolean;
}

/**
 * Детерминированный ответ по контексту.
 *
 * Это и есть проверка правила Л4: продукт отвечает на главный вопрос и без
 * модели — «сколько свободно, что сейчас главное и что делать первым».
 * Модель добавляет гладкость и понимание вопроса, а не смысл.
 */
export function fallbackAnswer(ctx: AdvisorContext, tool?: ToolResult): AdvisorAnswer {
  if (tool) {
    const verdict = tool.facts.verdict;
    const left = tool.facts.leftAfter;
    const headline = verdict === 'safe'
      ? 'Можно себе позволить'
      : verdict === 'tight' ? 'Можно, но будет тесно' : 'Сейчас не хватит';
    return {
      headline,
      basis: typeof left === 'number'
        ? `После этого останется ${formatMoney(left as Money)}, минимум по траектории ${formatMoney(tool.facts.minBalance as Money)}`
        : 'Расчёт выполнен по текущим данным',
      action: ctx.priorities.primary.action,
      limits: tool.limits,
      alternatives: tool.alternatives ?? [],
      withoutModel: true,
    };
  }

  const p = ctx.priorities.primary;
  const runwayNote = ctx.runway.known && ctx.runway.days !== undefined
    ? `Денег хватит примерно на ${ctx.runway.days} дн.`
    : ctx.runway.action ?? '';

  return {
    headline: p.title,
    basis: p.why,
    action: p.action,
    limits: [
      ...(ctx.missing.length > 0 ? [`Не хватает данных: ${ctx.missing.join(', ')}`] : []),
      ...(runwayNote && !ctx.runway.known ? [runwayNote] : []),
    ],
    alternatives: ctx.priorities.next.map((n) => ({
      label: n.title,
      effect: n.why,
    })),
    fine: whatIsFine(ctx),
    withoutModel: true,
  };
}

/**
 * Что в порядке — коротко и по фактам.
 *
 * Разбор из одних проблем оставляет человека в тревоге, даже когда почти всё
 * обеспечено. Это не утешение, а такой же результат расчёта: обеспеченный
 * платёж — сделанная работа, и не сказать о ней значит скрыть половину
 * картины.
 */
function whatIsFine(ctx: AdvisorContext): string[] {
  const out: string[] = [];

  const secured = ctx.payments.filter((p) => !p.settled && p.secured >= p.amount);
  if (secured.length > 0) {
    const names = secured.slice(0, 4).map((p) => p.label).join(', ');
    out.push(secured.length > 4
      ? `Деньги отложены: ${names} и ещё ${secured.length - 4}`
      : `Деньги отложены: ${names}`);
  }

  const paid = ctx.payments.filter((p) => p.settled);
  if (paid.length > 0) out.push(`Оплачено в этом цикле: ${paid.length}`);

  if (ctx.runway.known && (ctx.runway.days ?? 0) > 0) {
    out.push(`Денег хватит примерно на ${ctx.runway.days} дн.`);
  }

  return out;
}

/**
 * Проверка финансовых утверждений ответа.
 *
 * Каждое число в тексте обязано встречаться среди посчитанных фактов. Это не
 * стилистика: модель, пересказывая результат, легко «округляет» 18 400 до
 * 18 000, и такой ответ уже неверен.
 */
export function verifyNumbers(text: string, allowed: readonly (Money | number | string | null)[]): {
  ok: boolean;
  unknown: string[];
} {
  const allowedSet = new Set<string>();
  for (const v of allowed) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'number') {
      allowedSet.add(String(Math.abs(Math.round(v / 100))));   // рубли
      allowedSet.add(String(Math.abs(Math.round(v))));         // копейки
    } else {
      // Из строк вида «Аренда 36 000 ₽ 2026-09-20» берём все числа.
      for (const m of v.matchAll(/\d+/g)) allowedSet.add(m[0]);
    }
  }

  const unknown: string[] = [];
  // Пробелы внутри числа — разрядные: «18 400» это одно число, а не два.
  const normalized = text.replace(/(\d)[\s ](?=\d{3}\b)/g, '$1');
  for (const m of normalized.matchAll(/\d+/g)) {
    const n = m[0];
    // Однозначные и двузначные числа — это дни, проценты и порядковые:
    // проверять их бессмысленно, они не финансовые утверждения.
    if (n.length <= 2) continue;
    if (!allowedSet.has(n)) unknown.push(n);
  }
  return { ok: unknown.length === 0, unknown };
}

/** Сколько раундов инструментов разрешено на один аналитический ход. */
export const MAX_TOOL_ROUNDS = 3;
